/* ============================================================
 * MB FAVOLOSO - <lion-fbt-bundle>
 * Fix slider: mantiene root .fbt-bundle e carica layout orizzontale
 * ============================================================ */

class FbtBundle extends HTMLElement {
  constructor() {
    super();
    this.url = this.dataset.url;
    this.loaded = false;
    this.pendingVariant = null;

    this.io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !this.loaded) {
          this.loaded = true;
          this.load();
          this.io.disconnect();
          this.io = null;
        }
      },
      { rootMargin: '0px 0px 400px 0px' }
    );

    this.io.observe(this);

    this.variantChangeUnsub = window.subscribe?.(
      window.PUB_SUB_EVENTS?.variantChange,
      ({ variant }) => {
        if (!variant) return;

        if (!this.loaded) {
          this.pendingVariant = variant;
          return;
        }

        this.applyVariantToSelf(variant);
      }
    );
  }

  disconnectedCallback() {
    this.io?.disconnect();
    this.io = null;
    this.variantChangeUnsub?.();
  }

  applyVariantToSelf(variant) {
    const selfItem = this.querySelector('[data-fbt-item][data-self]');
    if (!selfItem) return;

    selfItem.dataset.variantId = String(variant.id);
    selfItem.dataset.price = String(variant.price);

    const price = selfItem.querySelector('.fbt-bundle__price');
    if (price) price.textContent = this.formatMoney(variant.price);

    this.recomputeTotal();
  }

  async load() {
    try {
      const res = await fetch(this.url, { headers: { Accept: 'text/html' } });
      if (!res.ok) throw new Error(res.statusText);

      const html = await res.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const root = doc.querySelector('[data-fbt-root]');

      if (!root) {
        this.hidden = true;
        return;
      }

      this.replaceChildren(root);
      this.wireUp();

      if (this.pendingVariant) {
        this.applyVariantToSelf(this.pendingVariant);
        this.pendingVariant = null;
      } else {
        this.recomputeTotal();
      }

      this.updateSliderButtons();
    } catch (e) {
      console.warn('[lion-fbt-bundle] load failed:', e);
      this.hidden = true;
    }
  }

  wireUp() {
    this.querySelectorAll('[data-fbt-toggle]').forEach((cb) => {
      cb.addEventListener('change', () => this.recomputeTotal());
    });

    const btn = this.querySelector('[data-fbt-add]');
    btn?.addEventListener('click', () => this.addBundleToCart(btn));

    const track = this.querySelector('[data-fbt-track]');
    const prev = this.querySelector('[data-fbt-prev]');
    const next = this.querySelector('[data-fbt-next]');

    prev?.addEventListener('click', () => this.scrollTrack(-1));
    next?.addEventListener('click', () => this.scrollTrack(1));

    track?.addEventListener('scroll', () => this.updateSliderButtons(), { passive: true });
    window.addEventListener('resize', () => this.updateSliderButtons());
  }

  scrollTrack(direction) {
    const track = this.querySelector('[data-fbt-track]');
    if (!track) return;

    const card = track.querySelector('[data-fbt-item]');
    const amount = card ? card.getBoundingClientRect().width + 12 : track.clientWidth * 0.75;

    track.scrollBy({
      left: amount * direction,
      behavior: 'smooth',
    });

    window.setTimeout(() => this.updateSliderButtons(), 280);
  }

  updateSliderButtons() {
    const track = this.querySelector('[data-fbt-track]');
    const prev = this.querySelector('[data-fbt-prev]');
    const next = this.querySelector('[data-fbt-next]');

    if (!track || !prev || !next) return;

    const maxScroll = track.scrollWidth - track.clientWidth;
    const hasOverflow = maxScroll > 4;

    prev.hidden = !hasOverflow;
    next.hidden = !hasOverflow;

    if (!hasOverflow) return;

    prev.disabled = track.scrollLeft <= 2;
    next.disabled = track.scrollLeft >= maxScroll - 2;
  }

  getSelectedItems() {
    const items = [];

    const selfItem = this.querySelector('[data-fbt-item][data-self]');
    if (selfItem) {
      items.push({
        id: Number(selfItem.dataset.variantId),
        quantity: 1,
      });
    }

    this.querySelectorAll('[data-fbt-item]:not([data-self])').forEach((item) => {
      const cb = item.querySelector('[data-fbt-toggle]');

      if (cb?.checked && !cb.disabled) {
        const vid = Number(item.dataset.variantId);
        if (Number.isFinite(vid) && vid > 0) {
          items.push({
            id: vid,
            quantity: 1,
          });
        }
      }
    });

    return items;
  }

  recomputeTotal() {
    const totalEl = this.querySelector('[data-fbt-total]');
    if (!totalEl) return;

    let totalCents = 0;

    const selfItem = this.querySelector('[data-fbt-item][data-self]');
    if (selfItem) totalCents += parseInt(selfItem.dataset.price, 10) || 0;

    this.querySelectorAll('[data-fbt-item]:not([data-self])').forEach((item) => {
      const cb = item.querySelector('[data-fbt-toggle]');
      if (cb?.checked && !cb.disabled) {
        totalCents += parseInt(item.dataset.price, 10) || 0;
      }
    });

    totalEl.textContent = this.formatMoney(totalCents);
  }

  formatMoney(cents) {
    const code = window.Shopify?.currency?.active || 'EUR';
    const locale = document.documentElement.lang || 'it';

    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: code,
      }).format(cents / 100);
    } catch {
      return `€${(cents / 100).toFixed(2).replace('.', ',')}`;
    }
  }

  async addBundleToCart(button) {
    const items = this.getSelectedItems();
    if (!items.length) return;

    const err = this.querySelector('[data-fbt-error]');
    if (err) {
      err.hidden = true;
      err.textContent = '';
    }

    const label = this.querySelector('[data-fbt-add-label]');
    const original = label?.textContent;

    button.disabled = true;
    if (label) label.textContent = 'Aggiungo…';

    try {
      const root = window.Shopify?.routes?.root || '/';
      const res = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({ items }),
      });

      const data = await res.json();

      if (!res.ok) {
        const msg = data?.description || data?.message || 'Impossibile aggiungere i prodotti selezionati.';
        throw new Error(msg);
      }

      window.publish?.(window.PUB_SUB_EVENTS?.cartUpdate, {
        source: 'lion-fbt-bundle',
      });

      document.dispatchEvent(new CustomEvent('cart:refresh'));
    } catch (e) {
      console.warn('[lion-fbt-bundle] add failed:', e);

      if (err) {
        err.textContent = e.message || 'Impossibile aggiungere i prodotti selezionati.';
        err.hidden = false;
      }
    } finally {
      button.disabled = false;
      if (label && original) label.textContent = original;
    }
  }
}

if (!customElements.get('lion-fbt-bundle')) {
  customElements.define('lion-fbt-bundle', FbtBundle);
}
